import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../../core/auth/auth.service';
import { InventoryStatus } from '../../../../models/enums';
import { InventoryDevice } from '../../../../models/interfaces';
import { InventoriesService } from '../../services/inventories.service';

/**
 * Contenu de création rapide d'un équipement affiché DANS un `app-popover`
 * ouvert depuis la sidenav globale. Pas de backdrop/positionnement ici —
 * c'est le popover parent qui s'en charge.
 *
 * Création minimale : label + statut « Disponible » + organisation courante.
 * `InventoriesService.addDevice` retourne l'objet créé et notifie lui-même
 * le succès (pas de notification supplémentaire ici).
 */
@Component({
  selector: 'app-quick-inventory-popover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="quick-popover">
      <div class="quick-popover-title">Nouvel équipement</div>

      <label class="quick-field">
        <span class="quick-field-label">Nom</span>
        <input
          #labelInput
          type="text"
          class="quick-input"
          placeholder="Nom de l'équipement"
          [value]="label()"
          [disabled]="submitting()"
          (input)="label.set($any($event.target).value)"
          (keydown.enter)="onSubmit()"
        />
      </label>

      @if (error()) {
        <div class="quick-error">{{ error() }}</div>
      }

      <div class="quick-popover-footer">
        <button type="button" class="quick-btn quick-btn-ghost" (click)="onCancel()">
          Annuler
        </button>
        <button
          type="button"
          class="quick-btn quick-btn-primary"
          [disabled]="!canSubmit()"
          (click)="onSubmit()"
        >
          {{ submitting() ? 'Création…' : 'Créer' }}
        </button>
      </div>
    </div>
  `,
  styles: `
    .quick-popover {
      width: min(280px, calc(100vw - 48px));
      /* max-width borne la largeur quand le panneau parent est contraint (mobile) */
      max-width: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 15px;
      box-sizing: border-box;
    }

    .quick-popover-title {
      font-family: var(--font-display);
      font-size: 1rem;
      font-weight: 800;
      color: var(--text-primary);
    }

    .quick-field {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .quick-field-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--text-secondary);
      padding: 0 2px;
    }

    .quick-input {
      height: 36px;
      box-sizing: border-box;
      width: 100%;
      border: 1px solid var(--border-strong);
      border-radius: 12px;
      background: var(--surface-soft);
      padding: 0 12px;
      font: inherit;
      font-size: 0.8125rem;
      color: var(--text-primary);
      transition: border-color 0.15s ease;

      &::placeholder {
        color: var(--text-tertiary);
      }

      &:focus {
        outline: none;
        border-color: var(--focus-ring-color);
        box-shadow: 0 0 0 3px var(--tint-orange);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .quick-error {
      font-size: 0.7rem;
      /* red-500 sur fond blanc/noir ne passe pas 4.5:1 (~3.76:1 en light) ;
         red-600 passe dans les deux thèmes (audit dark-mode-reviewer). */
      color: var(--red-600);
      padding: 0 2px;
    }

    .quick-popover-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      padding-top: 4px;
    }

    .quick-btn {
      height: 36px;
      padding: 0 16px;
      border-radius: var(--radius-pill);
      border: 1px solid transparent;
      font: inherit;
      font-size: 0.8125rem;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.15s, opacity 0.15s;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .quick-btn:focus-visible {
      outline: 2px solid var(--focus-ring-color);
      outline-offset: 2px;
    }

    .quick-btn-ghost {
      background: none;
      border-color: var(--border-strong);
      color: var(--text-primary);

      &:hover:not(:disabled) {
        background: var(--surface-soft);
        color: var(--text-primary);
      }
    }

    /* Action principale : café + crème (blanc sur orange ne tient pas le contraste). */
    .quick-btn-primary {
      background: var(--action-bg);
      color: var(--action-fg);

      &:hover:not(:disabled) {
        background: var(--action-bg-hover);
      }
    }
  `,
})
export class QuickInventoryPopoverComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private inventoriesService = inject(InventoriesService);

  created = output<void>();
  dismissed = output<void>();

  private labelInput = viewChild<ElementRef<HTMLInputElement>>('labelInput');

  label = signal('');
  submitting = signal(false);
  error = signal<string | null>(null);

  canSubmit = computed(() => this.label().trim().length > 0 && !this.submitting());

  constructor() {
    // preventScroll : sans lui, le focus fait défiler horizontalement le rail
    // (overflow hidden reste scrollable par programme) et décale tout le drawer
    afterNextRender(() => this.labelInput()?.nativeElement.focus({ preventScroll: true }));
  }

  onCancel(): void {
    this.dismissed.emit();
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;

    const label = this.label().trim();
    if (!label) return;

    this.submitting.set(true);
    this.error.set(null);

    try {
      const device = await this.inventoriesService.addDevice({
        label,
        status: InventoryStatus.Available,
        organisation_id: this.authService.getOrganisationId(),
      } as InventoryDevice);

      if (!device) {
        this.error.set("Impossible de créer l'équipement : réessayez dans un instant.");
        return;
      }

      await this.router.navigate(['/materials']);
      await this.router.navigate(
        [{ outlets: { sidebar: ['inventory-details', device.id] } }],
        { queryParamsHandling: 'merge' },
      );

      this.created.emit();
    } catch (err) {
      console.error('Error creating inventory device from quick popover:', err);
      this.error.set("Impossible de créer l'équipement : réessayez dans un instant.");
    } finally {
      this.submitting.set(false);
    }
  }
}
